/**
 * Weighted average cost of capital.
 *
 *   WACC = E/V × Re + D/V × Rd × (1 − Tc) + P/V × Rp
 *
 * E, D and P are the market values of common equity, debt and preferred stock and V their
 * sum. Re is the cost of equity (often from CAPM), Rd the pre-tax cost of debt, Rp the cost
 * of preferred stock (dividend ÷ price) and Tc the corporate tax rate, which turns the pre-tax
 * cost of debt into an after-tax cost because interest is tax-deductible. Rates are decimals.
 */

export interface WaccInput {
  equity: number;
  debt: number;
  /** Zero when the firm has no preferred stock. */
  preferred: number;
  costEquity: number;
  costDebt: number;
  costPreferred: number;
  taxRate: number;
}

export type ComponentKey = "equity" | "debt" | "preferred";

export interface WaccComponent {
  key: ComponentKey;
  value: number;
  /** Share of total capital, as a decimal. */
  weight: number;
  /** The rate before tax. */
  cost: number;
  /** The rate that enters the WACC: after tax for debt, unchanged otherwise. */
  effectiveCost: number;
  /** weight × effectiveCost. */
  contribution: number;
}

export interface WaccResult {
  total: number;
  components: WaccComponent[];
  wacc: number;
  /** WACC if interest were not deductible (tax rate 0). */
  waccBeforeTax: number;
  /** waccBeforeTax − wacc: the cost of capital the interest deduction removes. */
  taxShieldEffect: number;
  afterTaxCostOfDebt: number;
}

/** Null when a value is negative, there is no capital or the tax rate is outside 0–100%. */
export function wacc(input: WaccInput): WaccResult | null {
  const { equity, debt, preferred, taxRate } = input;
  const total = equity + debt + preferred;
  if (
    equity < 0 ||
    debt < 0 ||
    preferred < 0 ||
    !(total > 0) ||
    taxRate < 0 ||
    taxRate > 1
  ) {
    return null;
  }
  const afterTaxCostOfDebt = input.costDebt * (1 - taxRate);
  const raw: [ComponentKey, number, number, number][] = [
    ["equity", equity, input.costEquity, input.costEquity],
    ["debt", debt, input.costDebt, afterTaxCostOfDebt],
    ["preferred", preferred, input.costPreferred, input.costPreferred],
  ];
  const components = raw
    .filter(([, value]) => value > 0)
    .map(([key, value, cost, effectiveCost]) => {
      const weight = value / total;
      return {
        key,
        value,
        weight,
        cost,
        effectiveCost,
        contribution: weight * effectiveCost,
      };
    });
  const result = components.reduce((sum, c) => sum + c.contribution, 0);
  const before = components.reduce((sum, c) => sum + c.weight * c.cost, 0);
  return {
    total,
    components,
    wacc: result,
    waccBeforeTax: before,
    taxShieldEffect: before - result,
    afterTaxCostOfDebt,
  };
}
